import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Check, X, ArrowRight, ShieldCheck, Video, 
  FileCheck, AlertTriangle, Target, Eye, Building2,
  CreditCard, ArrowLeft, Scale, AlertCircle, 
  FileText, UserCheck, CheckCircle, XCircle, Rocket
} from 'lucide-react';
import CheckoutModal from '../components/landing/CheckoutModal';
import AfterPaymentBlock from '../components/catalog/AfterPaymentBlock';
import { usePrices } from '../context/PricesContext';

// Before/After items
const beforeItems = [
  { text: 'Не понимаешь, примет ли Amazon инвойс' },
  { text: 'Сомневаешься в поставщике' },
  { text: 'Каждая поставка — риск' },
  { text: 'Бизнес без предсказуемости' },
];

const afterItems = [
  { text: 'Понимаешь, как Amazon оценивает поставщика' },
  { text: 'Видишь инвойс глазами Amazon' },
  { text: 'Убираешь red flags заранее' },
  { text: 'Строишь цепочку поставок «в долгую»' },
];

// Result items
const resultItems = [
  'понимаете, как Amazon принимает решения при проверках',
  'видите инвойс и поставщика глазами Amazon',
  'заранее убираете red flags',
  'снижаете риск жалоб и отказов',
  'выстраиваете цепочку поставок без догадок и страха',
];

// 3 criteria cards
const criteriaCards = [
  {
    icon: FileText,
    title: 'Инвойс',
    desc: 'подлинность, структура, неизменность',
    color: 'blue',
  },
  {
    icon: Building2,
    title: 'Поставщик',
    desc: 'надёжность, бизнес-след, соответствие',
    color: 'violet',
  },
  {
    icon: CheckCircle,
    title: 'Совпадение критериев',
    desc: 'финальное решение APPROVED / REJECTED',
    color: 'emerald',
  },
];

// Course content
const courseContent = [
  {
    num: 1,
    title: 'Видео 1. Критерии Amazon к поставщику',
    desc: 'Более 10 факторов, которые реально учитываются при проверках',
    icon: UserCheck,
  },
  {
    num: 2,
    title: 'Видео 2. Критерии Amazon к инвойсу',
    desc: 'Структура, данные, детали, которые влияют на решение',
    icon: FileCheck,
  },
];

const SupplierInvoicePage = () => {
  const [checkoutData, setCheckoutData] = useState({ isOpen: false, packageId: '', packageName: '', price: 0 });
  const { getPrice } = usePrices();
  const price = getPrice('supplier_invoice');

  const handleCheckout = () => {
    setCheckoutData({ 
      isOpen: true, 
      packageId: 'supplier-invoice', 
      packageName: 'Поставщик и инвойс', 
      price: price 
    });
  };

  const closeCheckout = () => {
    setCheckoutData({ isOpen: false, packageId: '', packageName: '', price: 0 });
  };

  return (
    <div className="min-h-screen bg-[#050506]">
      {/* Checkout Modal */}
      <CheckoutModal 
        isOpen={checkoutData.isOpen}
        onClose={closeCheckout}
        packageId={checkoutData.packageId}
        packageName={checkoutData.packageName}
        price={checkoutData.price}
        discountMode={false}
      />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl blur opacity-40 group-hover:opacity-70 transition-opacity" />
                <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
                  <span className="text-black font-black text-lg">A</span>
                </div>
              </div>
              <div>
                <span className="text-white font-bold text-lg group-hover:text-amber-400 transition-colors">Amazon</span>
                <span className="text-zinc-400 font-medium text-lg ml-1">Wholesale</span>
              </div>
            </Link>
            
            <Link 
              to="/catalog"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-700 transition-all"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">В каталог</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Product Card */}
          <div className="relative mb-16">
            {/* Glow */}
            <div className="absolute -inset-3 bg-gradient-to-r from-blue-500/20 via-indigo-500/30 to-blue-500/20 rounded-[2rem] blur-2xl" />
            
            {/* Card */}
            <div className="relative bg-gradient-to-b from-zinc-900 to-zinc-950 rounded-[1.5rem] border border-blue-500/30 overflow-hidden">
              
              {/* Header */}
              <div className="relative p-8 sm:p-10 bg-gradient-to-b from-blue-500/10 to-transparent border-b border-blue-500/20">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                  <div className="flex items-center gap-5">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-xl shadow-blue-500/30">
                      <FileCheck className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                    </div>
                    <div>
                      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white leading-tight">
                        Как Amazon реально проверяет<br />
                        <span className="text-blue-400">поставщика и инвойс</span>
                      </h1>
                      <p className="text-zinc-400 text-base sm:text-lg mt-2">
                        и по каким критериям принимает решение APPROVED / REJECTED
                      </p>
                    </div>
                  </div>
                  <div className="text-left lg:text-right">
                    <p className="text-5xl sm:text-6xl font-black text-blue-400">${price}</p>
                    <p className="text-zinc-500 text-sm mt-1">единоразово</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 sm:p-10 space-y-10">
                
                {/* Subheadline */}
                <div className="p-6 rounded-2xl bg-gradient-to-r from-red-500/10 to-red-900/10 border border-red-500/30">
                  <p className="text-lg sm:text-xl text-zinc-300 leading-relaxed">
                    <span className="text-red-400 font-semibold">Если вы не понимаете, что именно Amazon проверяет,</span><br />
                    любой инвойс и поставка — это риск.
                  </p>
                </div>

                {/* Target Audience */}
                <div className="grid lg:grid-cols-2 gap-6">
                  <div className="p-6 rounded-2xl bg-emerald-500/5 border border-emerald-500/20">
                    <h4 className="text-emerald-400 font-bold text-lg sm:text-xl mb-4 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Для кого этот продукт
                    </h4>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-zinc-300">Amazon FBA селлеры (EU / US)</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-zinc-300">Уже продавали или продают</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-zinc-300">Сталкивались с жалобами, отказами</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-zinc-300">Сомневаются по инвойсам / поставщикам</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="p-6 rounded-2xl bg-red-500/5 border border-red-500/20">
                    <h4 className="text-red-400 font-bold text-lg sm:text-xl mb-4 flex items-center gap-2">
                      <XCircle className="w-5 h-5" />
                      НЕ подойдёт
                    </h4>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <span className="text-zinc-400">Новичкам без продаж</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <span className="text-zinc-400">Тем, кто работает с чеками</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <span className="text-zinc-400">Ищущим «хаки» и серые схемы</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Problem Block */}
                <div className="p-6 rounded-2xl bg-zinc-800/50 border border-zinc-700">
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                    <AlertTriangle className="w-6 h-6 text-amber-400" />
                    Проблема
                  </h3>
                  <p className="text-zinc-300 text-base sm:text-lg leading-relaxed">
                    Amazon проверяет не только инвойс, но и <span className="text-white font-semibold">всю цепочку поставки</span>.<br />
                    При этом требования не описаны прямо, а отказы часто шаблонные.<br />
                    <span className="text-amber-400">Без понимания логики проверки легко получить отказ или жалобу.</span>
                  </p>
                </div>

                {/* How Amazon Decides - 3 Cards */}
                <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6 flex items-center gap-3">
                    <Eye className="w-7 h-7 text-blue-400" />
                    Как Amazon принимает решение
                  </h3>
                  <p className="text-zinc-400 text-lg mb-6">Amazon оценивает 3 вещи:</p>
                  
                  <div className="grid md:grid-cols-3 gap-4">
                    {criteriaCards.map((card, i) => {
                      const colorStyles = {
                        blue: 'from-blue-500/10 to-blue-900/20 border-blue-500/30',
                        violet: 'from-violet-500/10 to-violet-900/20 border-violet-500/30',
                        emerald: 'from-emerald-500/10 to-emerald-900/20 border-emerald-500/30',
                      };
                      const iconColors = {
                        blue: 'from-blue-500 to-indigo-600 text-blue-400',
                        violet: 'from-violet-500 to-purple-600 text-violet-400',
                        emerald: 'from-emerald-500 to-teal-600 text-emerald-400',
                      };
                      
                      return (
                        <div 
                          key={i}
                          className={`p-6 rounded-2xl bg-gradient-to-b ${colorStyles[card.color]} border text-center`}
                        >
                          <div className={`w-14 h-14 mx-auto mb-4 rounded-xl bg-gradient-to-br ${iconColors[card.color].split(' ')[0]} ${iconColors[card.color].split(' ')[1]} flex items-center justify-center`}>
                            <card.icon className="w-7 h-7 text-white" />
                          </div>
                          <h4 className={`text-xl font-bold ${iconColors[card.color].split(' ')[2]} mb-2`}>{card.title}</h4>
                          <p className="text-zinc-400 text-sm">{card.desc}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Before/After Grid */}
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Before */}
                  <div className="p-6 rounded-2xl bg-red-500/5 border border-red-500/20">
                    <h4 className="text-red-400 font-bold text-xl sm:text-2xl mb-5 flex items-center gap-3">
                      <X className="w-6 h-6" />
                      ДО
                    </h4>
                    <div className="space-y-4">
                      {beforeItems.map((item, i) => (
                        <div key={i} className="flex items-start gap-4">
                          <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <X className="w-4 h-4 text-red-400" />
                          </div>
                          <span className="text-zinc-400 text-base sm:text-lg">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* After */}
                  <div className="p-6 rounded-2xl bg-emerald-500/5 border border-emerald-500/20">
                    <h4 className="text-emerald-400 font-bold text-xl sm:text-2xl mb-5 flex items-center gap-3">
                      <Check className="w-6 h-6" />
                      ПОСЛЕ
                    </h4>
                    <div className="space-y-4">
                      {afterItems.map((item, i) => (
                        <div key={i} className="flex items-start gap-4">
                          <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-4 h-4 text-emerald-400" />
                          </div>
                          <span className="text-zinc-300 text-base sm:text-lg">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* What's Included */}
                <div className="p-6 sm:p-8 rounded-2xl bg-gradient-to-b from-blue-500/10 to-blue-900/10 border border-blue-500/30">
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                    Что вы получите
                  </h3>
                  <p className="text-zinc-400 text-base sm:text-lg mb-6">
                    Всего <span className="text-white font-semibold">2 видео-урока</span>, которые дают полное понимание логики проверок Amazon.
                  </p>
                  <div className="space-y-4">
                    {courseContent.map((item) => (
                      <div 
                        key={item.num}
                        className="flex items-start gap-4 p-5 rounded-xl bg-zinc-800/50 border border-zinc-700/50"
                      >
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                          <item.icon className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <p className="text-white font-bold text-lg sm:text-xl">{item.title}</p>
                          <p className="text-zinc-400 text-base sm:text-lg">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Result Section */}
                <div className="p-6 sm:p-8 rounded-2xl bg-gradient-to-b from-blue-500/10 to-blue-900/10 border border-blue-500/30">
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6 flex items-center gap-3">
                    <Rocket className="w-7 h-7 text-blue-400" />
                    РЕЗУЛЬТАТ
                  </h3>
                  <p className="text-zinc-300 text-base sm:text-lg mb-4">После прохождения курса вы:</p>
                  <div className="space-y-3">
                    {resultItems.map((item, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-zinc-800/50">
                        <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        <span className="text-zinc-300 text-sm sm:text-base">{item}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-6 text-blue-400 font-bold text-lg sm:text-xl text-center">
                    Итог: предсказуемость и контроль вместо лотереи.
                  </p>
                </div>

                {/* After Payment Block */}
                <AfterPaymentBlock />

                {/* CTA Section */}
                <div className="text-center space-y-6 pt-8 border-t border-zinc-800">
                  
                  {/* Price Block */}
                  <div className="inline-block p-6 sm:p-8 rounded-2xl bg-gradient-to-b from-blue-500/10 to-blue-900/20 border border-blue-500/30">
                    <p className="text-5xl sm:text-6xl font-black text-blue-400 mb-4">${price}</p>
                    
                    {/* Buy Button */}
                    <button
                      onClick={handleCheckout}
                      data-testid="supplier-invoice-buy-btn"
                      className="w-full flex items-center justify-center gap-3 px-10 py-5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-black text-xl rounded-2xl hover:shadow-2xl hover:shadow-blue-500/30 hover:-translate-y-1 transition-all duration-300 mb-6"
                    >
                      <CreditCard className="w-6 h-6" />
                      <span>Купить сейчас</span>
                      <ArrowRight className="w-6 h-6" />
                    </button>
                    
                    {/* Value Propositions */}
                    <div className="space-y-3 text-left">
                      <div className="flex items-start gap-3">
                        <Target className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        <p className="text-zinc-400 text-sm">
                          <span className="text-blue-400 font-semibold">Понимание логики</span> = предсказуемость бизнеса
                        </p>
                      </div>
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <p className="text-zinc-400 text-sm">
                          <span className="text-red-400 font-semibold">Без этих знаний</span> каждая поставка — риск
                        </p>
                      </div>
                      <div className="flex items-start gap-3">
                        <Scale className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <p className="text-zinc-400 text-sm">
                          <span className="text-emerald-400 font-semibold">Критерии Amazon</span> без догадок и риска
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Guarantee */}
                  <div className="flex items-center justify-center gap-2 text-zinc-500 text-base">
                    <ShieldCheck className="w-5 h-5 text-emerald-400" />
                    <span>Безопасная оплата через Stripe</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Back to catalog */}
          <div className="text-center">
            <Link 
              to="/catalog"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-700 transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Вернуться в каталог</span>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SupplierInvoicePage;
